// <CalendarTab> — T-144 (initial) + T-147 (pixel polish).
//
// The body of the Calendar tab in the Time view: a horizontal-scroll
// roster of employees × the 7-day week starting at `weekStart`. Each
// cell is empty (no request) or shows a colored bar with the policy
// abbreviation. The toolbar (T-143) owns the URL state — this body
// reads `weekStart`, `site`, `team`, `type` and fetches
// `GET /api/timeoff/calendar` accordingly.
//
// Visual references — time-view-spec.md:
//   - §1.2 Policy colors (calendar bars)
//   - §1.3 Today indicators (wine-red #A6264C accent)
//   - §1.4 Calendar grid (availability row + today line)
//   - §3   Page structure
//   - §6   Calendar tab anatomy (sticky-left column, day cells, bars)
//
// T-147 (pixel polish) additions on top of T-144:
//   - Wine-red `#A6264C` accent on today's column tint + vertical line.
//   - Availability row above the roster — count of out-employees per
//     day with a tiny bar chart.
//   - Auto-scroll the visible today column into view on first render
//     when the current week is showing.

import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  CalendarResponse,
  TimeOffType,
} from '@pa/shared/time-view';
import { api, ApiError } from '../../api/client';
import {
  isoMonday,
  shiftIsoDate,
  useTimeViewUrlState,
} from './timeViewUrlState';

// Frozen-column + per-day widths. Bumped slightly above the spec's
// 200/45 to match the brief's "~180px / ~140px" guidance.
const STICKY_COL_W = 180;
const DAY_COL_W = 140;

// Time-view identity accent (spec §1).
const ACCENT = '#A6264C';

// Policy → Tailwind class for the colored bar. Default falls back to
// slate for unknown types so a future TimeOffType doesn't render a
// blank cell.
const POLICY_COLOR: Record<TimeOffType, string> = {
  Vacation: 'bg-orange-400 text-white',
  Sick: 'bg-red-500 text-white',
  'Child Sick': 'bg-purple-500 text-white',
  'Work from Home': 'bg-blue-500 text-white',
  'Parental Leave': 'bg-yellow-500 text-white',
};

// Abbreviation shown on the bar — keeps the label readable when the
// cell is narrow. Single letter where the policy has a distinctive
// first letter, two letters when there's a collision (Sick / Child
// Sick).
const POLICY_ABBR: Record<TimeOffType, string> = {
  Vacation: 'V',
  Sick: 'S',
  'Child Sick': 'CS',
  'Work from Home': 'WFH',
  'Parental Leave': 'P',
};

// Two-letter day-of-week labels rendered in the header row. Indexed
// Monday-first to match the API contract (`days[0]` is Monday).
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Today's ISO date in UTC (matches the calendar's UTC contract).
 */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function CalendarTab() {
  const { state } = useTimeViewUrlState();
  const { weekStart, site, team, type } = state;

  // Build the query string only with non-default params so the URL
  // matches what the server expects (omit "all" / undefined).
  const queryPath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('weekStart', weekStart);
    if (site !== 'all') params.set('siteId', site);
    if (team !== 'all') params.set('teamId', team);
    if (type !== 'all') params.set('requestType', type);
    return `/api/timeoff/calendar?${params.toString()}`;
  }, [weekStart, site, team, type]);

  const query = useQuery({
    queryKey: ['timeView', 'calendar', { weekStart, site, team, type }],
    queryFn: () => api.get<CalendarResponse>(queryPath),
  });

  // Derive 7 ISO dates for the header row. We pre-compute them off
  // `weekStart` instead of trusting the response's `rows[i].days[i]`
  // so the skeleton can render the header before the fetch resolves.
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => shiftIsoDate(weekStart, i)),
    [weekStart],
  );

  // Resolve today's index within the visible week (or -1 when today
  // falls outside it). Drives the today highlight + vertical line.
  const today = todayIso();
  const isCurrentWeek = weekStart === isoMonday();
  const todayIndex = useMemo(
    () => weekDates.findIndex((iso) => iso === today),
    [weekDates, today],
  );

  // Container we'll scrollIntoView-on-mount when the current week is
  // visible. Centers the today column horizontally.
  const todayCellRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!isCurrentWeek || todayIndex < 0) return;
    const node = todayCellRef.current;
    if (!node) return;
    // jsdom doesn't implement layout — guard against missing API.
    if (typeof node.scrollIntoView !== 'function') return;
    node.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [isCurrentWeek, todayIndex, queryPath]);

  if (query.isLoading) {
    return <LoadingSkeleton dates={weekDates} todayIndex={todayIndex} />;
  }

  if (query.isError) {
    // The two endpoints behind this view (`/api/timeoff/calendar` +
    // `/api/timeoff/requests`) gate to OWNER/ADMIN. A MEMBER who
    // lands here via direct URL gets a 403 — show a copy-clear
    // message instead of the generic "refresh the page" treatment.
    if (query.error instanceof ApiError && query.error.status === 403) {
      return (
        <div
          className="p-8 text-sm text-slate-600"
          data-testid="calendar-tab-forbidden"
        >
          <p className="font-medium text-slate-800 mb-1">
            People&rsquo;s time off is admin-only.
          </p>
          <p>
            Ask an OWNER or ADMIN in your organization for access. You
            can still see your own requests on the{' '}
            <a href="/time-off" className="text-[#6344C6] hover:underline">
              Time Off
            </a>{' '}
            page.
          </p>
        </div>
      );
    }
    const errorMsg = query.error instanceof Error
      ? query.error.message
      : 'Unknown error';
    const isDiagnostic = errorMsg && errorMsg !== 'Unknown error';
    return (
      <div
        className="p-8 text-sm"
        data-testid="calendar-tab-error"
      >
        <p className="text-red-600 mb-2">
          Failed to load the calendar. Try refreshing the page.
        </p>
        {isDiagnostic && (
          <p className="text-xs text-red-500 font-mono mt-3 p-2 bg-red-50 rounded">
            Error: {errorMsg}
          </p>
        )}
      </div>
    );
  }

  const data = query.data;
  if (!data || data.rows.length === 0) {
    return (
      <div className="flex flex-col" data-testid="calendar-tab">
        <HeaderRow dates={weekDates} todayIndex={todayIndex} />
        <div
          className="p-8 text-sm text-slate-500"
          data-testid="calendar-tab-empty"
        >
          No employees match the current filters.
        </div>
      </div>
    );
  }

  // Map employeeId → metadata for the row chrome (avatar + name).
  const empById = new Map(data.employees.map((e) => [e.id, e]));

  // Per-day count of out-employees — fuels the Availability row at
  // the top of the roster. We count rows where `days[i].type` is not
  // null; weekend days are included (the chart shows 0 most weeks).
  const outPerDay = Array.from({ length: 7 }, (_, i) =>
    data.rows.reduce((acc, row) => acc + (row.days[i]?.type ? 1 : 0), 0),
  );

  return (
    <div className="overflow-x-auto" data-testid="calendar-tab">
      <div
        className="min-w-max relative"
        style={{ minWidth: `${STICKY_COL_W + DAY_COL_W * 7}px` }}
      >
        <HeaderRow dates={weekDates} todayIndex={todayIndex} />
        <AvailabilityRow
          counts={outPerDay}
          maxCount={Math.max(1, data.rows.length)}
          todayIndex={todayIndex}
        />
        <div data-testid="calendar-rows" className="relative">
          {data.rows.map((row) => {
            const emp = empById.get(row.employeeId);
            return (
              <EmployeeRow
                key={row.employeeId}
                employeeId={row.employeeId}
                name={emp?.name ?? '(unknown)'}
                avatarUrl={emp?.avatarUrl ?? null}
                days={row.days}
                todayIndex={todayIndex}
                todayCellRef={todayCellRef}
              />
            );
          })}
          {todayIndex >= 0 && <TodayVerticalLine todayIndex={todayIndex} />}
        </div>
      </div>
    </div>
  );
}

// ── pieces ──────────────────────────────────────────────────────────

function HeaderRow({
  dates,
  todayIndex,
}: {
  dates: string[];
  todayIndex: number;
}) {
  return (
    <div
      className="flex items-stretch border-b border-slate-200 bg-white sticky top-0 z-20"
      data-testid="calendar-header-row"
    >
      <div
        className="sticky left-0 z-30 bg-white border-r border-slate-200 px-4 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center"
        style={{ minWidth: `${STICKY_COL_W}px`, width: `${STICKY_COL_W}px` }}
      >
        Employees
      </div>
      {dates.map((iso, idx) => {
        const dow = idx; // 0 = Mon … 6 = Sun
        const isWeekend = dow >= 5;
        const isToday = idx === todayIndex;
        const dayNum = Number(iso.split('-')[2]);
        const bg = isToday
          ? 'bg-[#A6264C]/5'
          : isWeekend
            ? 'bg-slate-50'
            : 'bg-white';
        return (
          <div
            key={iso}
            data-testid={`calendar-header-day-${idx}`}
            data-iso={iso}
            data-today={isToday ? 'true' : 'false'}
            className={
              'flex flex-col items-center justify-center py-2 border-r border-slate-200 text-center ' +
              bg
            }
            style={{ minWidth: `${DAY_COL_W}px`, width: `${DAY_COL_W}px` }}
          >
            <span className="text-[11px] font-bold text-slate-600">
              {DAY_LABELS[idx]}
            </span>
            {isToday ? (
              <span
                data-testid="calendar-header-today-pill"
                className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold text-white"
                style={{ backgroundColor: ACCENT }}
              >
                {dayNum}
              </span>
            ) : (
              <span className="text-[10px] font-medium text-slate-400">
                {dayNum}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AvailabilityRow({
  counts,
  maxCount,
  todayIndex,
}: {
  counts: number[];
  maxCount: number;
  todayIndex: number;
}) {
  return (
    <div
      className="flex items-stretch border-b border-slate-100 bg-slate-50/40"
      data-testid="calendar-availability-row"
    >
      <div
        className="sticky left-0 z-10 bg-slate-50/80 border-r border-slate-200 px-4 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center"
        style={{ minWidth: `${STICKY_COL_W}px`, width: `${STICKY_COL_W}px` }}
      >
        Availability
      </div>
      {counts.map((count, idx) => {
        const isWeekend = idx >= 5;
        const isToday = idx === todayIndex;
        const pct = maxCount > 0 ? Math.min(100, (count / maxCount) * 100) : 0;
        return (
          <div
            key={idx}
            data-testid={`calendar-availability-day-${idx}`}
            data-count={count}
            className={
              'flex flex-col items-center justify-center px-2 py-2 border-r border-slate-100 text-center ' +
              (isToday
                ? 'bg-[#A6264C]/5'
                : isWeekend
                  ? 'bg-slate-50/60'
                  : 'bg-slate-50/40')
            }
            style={{ minWidth: `${DAY_COL_W}px`, width: `${DAY_COL_W}px` }}
          >
            <span className="text-[10px] font-semibold text-slate-500">
              {count} out
            </span>
            <div className="w-3/5 h-1 mt-1 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  backgroundColor: count > 0 ? ACCENT : 'transparent',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TodayVerticalLine({ todayIndex }: { todayIndex: number }) {
  // Centered through the today column. The pointer-events-none lets
  // the underlying cells still receive hovers/clicks.
  const left = STICKY_COL_W + DAY_COL_W * todayIndex + DAY_COL_W / 2;
  return (
    <div
      data-testid="calendar-today-line"
      aria-hidden
      className="absolute top-0 bottom-0 pointer-events-none"
      style={{
        left: `${left}px`,
        width: '1px',
        backgroundColor: ACCENT,
        zIndex: 5,
      }}
    />
  );
}

function EmployeeRow({
  employeeId,
  name,
  avatarUrl,
  days,
  todayIndex,
  todayCellRef,
}: {
  employeeId: string;
  name: string;
  avatarUrl: string | null;
  days: Array<{ date: string; type: TimeOffType | null }>;
  todayIndex: number;
  todayCellRef: React.MutableRefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      className="flex items-stretch border-b border-slate-100 hover:bg-slate-50/60 group"
      data-testid="calendar-employee-row"
      data-employee-id={employeeId}
    >
      <div
        className="sticky left-0 z-10 bg-white group-hover:bg-slate-50/60 border-r border-slate-200 px-3 py-2 flex items-center gap-2"
        style={{ minWidth: `${STICKY_COL_W}px`, width: `${STICKY_COL_W}px` }}
      >
        <Avatar name={name} avatarUrl={avatarUrl} />
        <span
          className="text-xs font-bold text-slate-800 truncate"
          title={name}
        >
          {name}
        </span>
      </div>
      {days.map((day, idx) => {
        const isWeekend = idx >= 5;
        const isToday = idx === todayIndex;
        const bg = isToday
          ? 'bg-[#A6264C]/5'
          : isWeekend
            ? 'bg-slate-50/80'
            : 'bg-white';
        // First employee row attaches the ref to today's cell so the
        // parent can `scrollIntoView` it once mounted. Subsequent rows
        // re-set the ref harmlessly — last writer wins, which is fine.
        const setRef =
          isToday && employeeId
            ? (node: HTMLDivElement | null) => {
                todayCellRef.current = node;
              }
            : undefined;
        return (
          <div
            key={day.date}
            ref={setRef}
            data-testid={`calendar-cell-${employeeId}-${idx}`}
            data-iso={day.date}
            data-today={isToday ? 'true' : 'false'}
            className={
              'h-14 p-1 border-r border-slate-100 flex items-center ' + bg
            }
            style={{ minWidth: `${DAY_COL_W}px`, width: `${DAY_COL_W}px` }}
          >
            {day.type ? <PolicyBar type={day.type} /> : null}
          </div>
        );
      })}
    </div>
  );
}

function PolicyBar({ type }: { type: TimeOffType }) {
  const color = POLICY_COLOR[type] ?? 'bg-slate-200 text-slate-600';
  const abbr = POLICY_ABBR[type] ?? '?';
  return (
    <div
      data-testid="calendar-policy-bar"
      data-policy={type}
      title={type}
      className={
        'h-6 w-full rounded flex items-center px-2 text-[10px] font-bold tracking-wide shadow-sm ' +
        color
      }
    >
      {abbr}
    </div>
  );
}

function Avatar({
  name,
  avatarUrl,
}: {
  name: string;
  avatarUrl: string | null;
}) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className="w-8 h-8 rounded-full object-cover bg-slate-100 flex-shrink-0"
      />
    );
  }
  // Fallback: monogram from the first letter (cheap; no name-parsing
  // edge cases since the server already filters out blank names).
  const initial = (name.trim()[0] ?? '?').toUpperCase();
  return (
    <div
      className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 text-xs font-bold flex items-center justify-center flex-shrink-0"
      aria-hidden
    >
      {initial}
    </div>
  );
}

function LoadingSkeleton({
  dates,
  todayIndex,
}: {
  dates: string[];
  todayIndex: number;
}) {
  return (
    <div data-testid="calendar-tab-loading">
      <HeaderRow dates={dates} todayIndex={todayIndex} />
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="flex items-stretch border-b border-slate-100"
        >
          <div
            className="bg-white border-r border-slate-200 px-3 py-2 flex items-center gap-2"
            style={{ minWidth: `${STICKY_COL_W}px`, width: `${STICKY_COL_W}px` }}
          >
            <div className="w-8 h-8 rounded-full bg-slate-100 animate-pulse" />
            <div className="h-3 w-24 rounded bg-slate-100 animate-pulse" />
          </div>
          {dates.map((iso, idx) => (
            <div
              key={iso}
              className={
                'h-14 p-1 border-r border-slate-100 ' +
                (idx >= 5 ? 'bg-slate-50/80' : 'bg-white')
              }
              style={{ minWidth: `${DAY_COL_W}px`, width: `${DAY_COL_W}px` }}
            >
              <div className="h-6 w-full rounded bg-slate-100 animate-pulse" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
